/**
 * Several styles in one upload (JF 2026-10-12): two different tech packs sent together were merged into one checklist.
 * The extract step names the style of every file; this groups the files by style so the AM can pick one.
 */
export interface FileStyle { file: number; style_no?: string; style_name?: string; category?: string; summary?: string }
/** A style the AI found, with the file and pages it is on ([] = the whole file) — JF 2026-10-13 */
export interface FoundStyle { style_no?: string; style_name?: string; category?: string; file: number; pages?: number[] }
/** pages = per file number, the pages of that file that describe this style (missing = the whole file) */
export interface StyleGroup { key: string; style_no: string; style_name: string; category: string; files: number[]; pages?: Record<string, number[]> }

const norm = (s: unknown) => String(s ?? "").toUpperCase().replace(/[^A-Z0-9一-鿿]+/g, "");

/**
 * Files grouped by style (style number, else style name; a number and a name that point to the same group merge).
 * Files without a style (a price list, an e-mail) are in `loose` — they go along with whichever style is picked.
 * `nFiles` = how many files were uploaded; numbers outside 1..nFiles are ignored.
 */
export function styleGroups(files: FileStyle[] | undefined, nFiles: number, styles?: FoundStyle[]): { groups: StyleGroup[]; loose: number[] } {
  // several styles inside one file (a design brief, a line sheet): the AI's style list with pages (JF 2026-10-13)
  const st = (styles || []).filter((x) => Math.round(Number(x.file)) >= 1 && Math.round(Number(x.file)) <= nFiles && (norm(x.style_no) || norm(x.style_name)));
  const byFile = new Map<number, number>();
  for (const x of st) byFile.set(Math.round(Number(x.file)), (byFile.get(Math.round(Number(x.file))) || 0) + 1);
  if ([...byFile.values()].some((k) => k > 1)) {
    const groups: StyleGroup[] = [];
    for (const x of st) {
      const n = Math.round(Number(x.file));
      const no = norm(x.style_no), name = norm(x.style_name);
      let g = groups.find((y) => (no && norm(y.style_no) === no) || (name && norm(y.style_name) === name));
      if (!g) { g = { key: no || name, style_no: String(x.style_no || ""), style_name: String(x.style_name || ""), category: String(x.category || ""), files: [], pages: {} }; groups.push(g); }
      if (!g.files.includes(n)) g.files.push(n);
      const pg = (x.pages || []).map((p) => Math.round(Number(p))).filter((p) => p > 0);
      if (pg.length) g.pages![String(n)] = [...new Set([...(g.pages![String(n)] || []), ...pg])].sort((a, b) => a - b);
    }
    const used = new Set(groups.flatMap((g) => g.files));
    const loose: number[] = [];
    for (let n = 1; n <= nFiles; n++) if (!used.has(n)) loose.push(n);
    return { groups, loose };
  }
  const groups: StyleGroup[] = [];
  const seen = new Set<number>();
  for (const f of files || []) {
    const n = Math.round(Number(f.file));
    if (!(n >= 1 && n <= nFiles) || seen.has(n)) continue;
    const no = norm(f.style_no), name = norm(f.style_name);
    if (!no && !name) continue;
    seen.add(n);
    const g = groups.find((x) => (no && norm(x.style_no) === no) || (name && norm(x.style_name) === name));
    if (g) {
      g.files.push(n);
      if (!g.style_no && f.style_no) g.style_no = String(f.style_no);
      if (!g.style_name && f.style_name) g.style_name = String(f.style_name);
      if (!g.category && f.category) g.category = String(f.category);
    } else groups.push({ key: no || name, style_no: String(f.style_no || ""), style_name: String(f.style_name || ""), category: String(f.category || ""), files: [n] });
  }
  const loose: number[] = [];
  for (let n = 1; n <= nFiles; n++) if (!seen.has(n)) loose.push(n);
  return { groups, loose };
}
